import React, { useState, useEffect } from 'react';
import {
  Plus,
  UserPlus,
  Gamepad2,
  Trash2,
  Share2,
  Youtube,
  Instagram,
  Facebook,
  Music2,
  AlertCircle,
  MoreVertical,
  CheckCircle2,
  Users,
  Settings,
  Link2,
  Info,
  Globe,
  ShoppingCart,
  Laptop,
  Gamepad,
  Wand2,
  Loader2,
  Search,
  ExternalLink,
  PlusCircle,
  MessageSquare,
  Send,
  Cloud,
  Flame,
  ChevronRight,
  Tag,
  RefreshCw,
  X
} from 'lucide-react';
import { useProjects, Project } from '../../contexts/ProjectContext';
import { usePostModal } from '../../contexts/PostModalContext';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import axios from 'axios';
import { toast } from "sonner";
import moment from 'moment';
import { cn } from "../../lib/utils";
import { API_BASE_URL, getArtworkUrl } from "../../config";
import { brand } from '../../config/brand';
import DowngradeBanner from '../../components/DowngradeBanner';

// shadcn UI
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";

const isGamedev = brand.features.showGamedevFeatures;

export default function Projects(): JSX.Element {
  const { projects, refreshProjects, setActiveProject, activeProject, loading } = useProjects();
  const { openPostById } = usePostModal();
  const { refreshUser } = useGlobalState();
  const token = localStorage.getItem('token');
  const [openCreate, setOpenCreate] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);
  const [openSocial, setOpenSocial] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [error, setError] = useState('');

  const [projectToArchive, setProjectToArchive] = useState<Project | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveDependencies, setArchiveDependencies] = useState<any>(null);
  const [checkingDependencies, setCheckingDependencies] = useState(false);

  // Auto-open new project modal if no projects exist
  useEffect(() => {
    if (!loading && projects.length === 0) {
      setOpenCreate(true);
    }
  }, [loading, projects.length]);

  const [newProject, setNewProject] = useState({ name: '', description: '', links: [] as any[], tags: [] as string[] });
  const [editData, setEditData] = useState<Partial<Project>>({ tags: [] });
  const [inviteData, setInviteData] = useState({ email: '', role: 'VIEWER' });
  const [createStep, setCreateStep] = useState<'name' | 'results'>('name');

  // Tag state
  const [newTag, setNewTag] = useState('');
  const [isScrapingTags, setIsScrapingTags] = useState(false);

  // Artwork state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [artworkPreview, setArtworkPreview] = useState<string | null>(null);
  const [isRegeneratingSummary, setIsRegeneratingSummary] = useState(false);

  // IGDB state
  const [isSearchingIgdb, setIsSearchingIgdb] = useState(false);
  const [igdbResults, setIgdbResults] = useState<any[]>([]);
  const [showIgdbResults, setShowIgdbResults] = useState(false);

  // Link management state
  const [newLinkUrl, setNewLinkUrl] = useState('');

  const [socialData, setSocialData] = useState({ platform: 'twitter', name: '', handle: '' });
  const [socialTab, setSocialTab] = useState("add-new");
  const [allSocialAccounts, setAllSocialAccounts] = useState<any[]>([]);

  const fetchAllSocialAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/social-accounts/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSocialAccounts(response.data);
    } catch (err) {
      console.error('Error fetching all social accounts:', err);
    }
  };

  useEffect(() => {
    if (openSocial) {
      fetchAllSocialAccounts();
    }
  }, [openSocial]);

  const getUrlHostname = (url: string) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch (e) {
      return url;
    }
  };

  const getUrlIcon = (url: string, className: string = "h-4 w-4") => {
    const u = url.toLowerCase();
    if (u.includes('twitter.com') || u.includes('x.com')) return (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M18.244 2.25h3.308l-7.227 3.779 8.502 11.221h-6.657l-5.214-6.817-5.966 6.817H1.676l7.728-8.831L1.242 2.25h6.826l4.704 6.231 5.472-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
      </svg>
    );
    if (u.includes('youtube.com') || u.includes('youtu.be')) return <Youtube className={className} />;
    if (u.includes('instagram.com')) return <Instagram className={className} />;
    if (u.includes('tiktok.com')) return <Music2 className={className} />;
    if (u.includes('threads.net') || u.includes('threads.com')) return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <path d="M12 12c0-3 2.5-5.5 5.5-5.5S23 9 23 12c0 3.5-3 6.5-6.5 6.5h-4.5c-3.5 0-6.5-3-6.5-6.5S8.5 5.5 12 5.5s6.5 3 6.5 6.5" />
      </svg>
    );
    if (u.includes('facebook.com')) return <Facebook className={className} />;
    if (u.includes('discord.gg') || u.includes('discord.com')) return <MessageSquare className={className} />;
    if (u.includes('t.me') || u.includes('telegram.me')) return <Send className={className} />;
    if (u.includes('twitch.tv')) return <Music2 className={className} />;
    if (u.includes('steampowered.com') || u.includes('steamcommunity.com')) return <ShoppingCart className={className} />;
    if (u.includes('itch.io')) return <Gamepad className={className} />;
    if (u.includes('playstation.com')) return <Gamepad className={className} />;
    if (u.includes('nintendo.com')) return <Laptop className={className} />;
    return <Globe className={className} />;
  };

  const getPlatformIcon = (platform: string, className: string = "h-4 w-4") => {
    switch (platform.toLowerCase()) {
      case 'twitter': case 'x': return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M18.244 2.25h3.308l-7.227 3.779 8.502 11.221h-6.657l-5.214-6.817-5.966 6.817H1.676l7.728-8.831L1.242 2.25h6.826l4.704 6.231 5.472-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
        </svg>
      );
      case 'youtube': return <Youtube className={className} />;
      case 'instagram': return <Instagram className={className} />;
      case 'threads': return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={className}
        >
          <path d="M12 12c0-3 2.5-5.5 5.5-5.5S23 9 23 12c0 3.5-3 6.5-6.5 6.5h-4.5c-3.5 0-6.5-3-6.5-6.5S8.5 5.5 12 5.5s6.5 3 6.5 6.5" />
        </svg>
      );
      case 'facebook': return <Facebook className={className} />;
      case 'tiktok': return <Music2 className={className} />;
      case 'bluesky': return <Cloud className={className} />;
      case 'discord': return <MessageSquare className={className} />;
      case 'telegram': return <Send className={className} />;
      default: return <Share2 className={className} />;
    }
  };

  const handleAddLink = (isNew: boolean = false) => {
    if (!newLinkUrl) return;
    const linkObj = { url: newLinkUrl };

    if (isNew) {
      setNewProject(prev => ({
        ...prev,
        links: [...(prev.links || []), linkObj]
      } as any));
    } else {
      setEditData(prev => ({
        ...prev,
        links: [...(prev.links || []), linkObj]
      }));
    }
    setNewLinkUrl('');
  };

  const handleRemoveLink = (index: number, isNew: boolean = false) => {
    if (isNew) {
      setNewProject(prev => ({
        ...prev,
        links: (prev.links || []).filter((_: any, i: number) => i !== index)
      } as any));
    } else {
      setEditData(prev => ({
        ...prev,
        links: (prev.links || []).filter((_: any, i: number) => i !== index)
      }));
    }
  };

  const handleAddTag = (isNew: boolean = false) => {
    if (!newTag.trim()) return;
    const tag = newTag.trim();

    if (isNew) {
      setNewProject(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      } as any));
    } else {
      setEditData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
    }
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove: string, isNew: boolean = false) => {
    if (isNew) {
      setNewProject(prev => ({
        ...prev,
        tags: (prev.tags || []).filter((t: string) => t !== tagToRemove)
      } as any));
    } else {
      setEditData(prev => ({
        ...prev,
        tags: (prev.tags || []).filter((t: string) => t !== tagToRemove)
      }));
    }
  };

  const handleScrapeSteamTags = async (url: string, isNew: boolean = false) => {
    if (!url || !url.includes('steampowered.com')) return;

    setIsScrapingTags(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/games/steam/tags?url=${encodeURIComponent(url)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const scrapedTags = response.data.tags || [];
      if (scrapedTags.length > 0) {
        if (isNew) {
          setNewProject(prev => ({
            ...prev,
            tags: Array.from(new Set([...(prev.tags || []), ...scrapedTags]))
          } as any));
        } else {
          setEditData(prev => ({
            ...prev,
            tags: Array.from(new Set([...(prev.tags || []), ...scrapedTags]))
          }));
        }
        toast.success(`Imported ${scrapedTags.length} tags from Steam!`);
      }
    } catch (err) {
      console.error('Error scraping Steam tags:', err);
    } finally {
      setIsScrapingTags(false);
    }
  };

  const handleCreateProject = async (overrideData?: any) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();

      const projectToCreate = overrideData || newProject;

      formData.append('name', projectToCreate.name);
      formData.append('description', projectToCreate.description || '');

      if (projectToCreate.links) {
        formData.append('links', JSON.stringify(projectToCreate.links));
      }

      if (projectToCreate.tags) {
        formData.append('tags', JSON.stringify(projectToCreate.tags));
      }

      if (selectedFile) {
        formData.append('artwork', selectedFile);
      } else if (projectToCreate.artworkUrl) {
        formData.append('artworkUrl', projectToCreate.artworkUrl);
      }

      const response = await axios.post(`${API_BASE_URL}/games`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      await refreshProjects();
      await refreshUser(); // Update Steam opportunities in global state
      setOpenCreate(false);
      setNewProject({ name: '', description: '', links: [], tags: [] });
      setSelectedFile(null);
      setArtworkPreview(null);
      setCreateStep('name');

      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error creating project');
      throw err;
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject) return;
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();

      Object.keys(editData).forEach(key => {
        const value = (editData as any)[key];
        if (value !== undefined && value !== null) {
          if (key === 'links' || key === 'tags') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      });

      if (selectedFile) {
        formData.append('artwork', selectedFile);
      }

      await axios.put(`${API_BASE_URL}/games/${selectedProject.id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      await refreshProjects();
      await refreshUser(); // Update Steam opportunities in global state
      setOpenEdit(false);
      setEditData({});
      setSelectedFile(null);
      setArtworkPreview(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error updating project');
    }
  };

  const handleInvite = async () => {
    if (!selectedProject) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/games/${selectedProject.id}/collaborators`, inviteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshProjects();
      setOpenInvite(false);
      setInviteData({ email: '', role: 'VIEWER' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error inviting user');
    }
  };

  const checkArchiveDependencies = async (project: Project) => {
    setProjectToArchive(project);
    setCheckingDependencies(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/games/${project.id}/dependencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchiveDependencies(res.data);
    } catch (err) {
      console.error('Failed to check dependencies:', err);
      // Fallback to simple confirmation if check fails
      if (window.confirm('Are you sure you want to archive this project? It will be removed from your active projects list.')) {
        await handleArchiveProject(project.id);
      }
    } finally {
      setCheckingDependencies(false);
    }
  };

  const handleArchiveProject = async (projectId: string) => {
    setIsArchiving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/games/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // If the archived project was the active one, clear it
      if (activeProject?.id === projectId) {
        setActiveProject(null);
      }

      await refreshProjects();
      setProjectToArchive(null);
      setArchiveDependencies(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error archiving project');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleSelectIgdbGame = async (igdbId: number, isNewProject: boolean = false) => {
    setIsSearchingIgdb(true);
    setShowIgdbResults(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/games/igdb/details/${igdbId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const details = response.data;

      if (isNewProject) {
        // If it's a new project, we AUTO-SAVE immediately
        const createdProject = await handleCreateProject(details);

        // After auto-saving, open the Edit modal for this project
        setSelectedProject(createdProject);
        setEditData({
          name: createdProject.name,
          description: createdProject.description || '',
          links: createdProject.links || [],
          websiteUrl: createdProject.websiteUrl,
          steamUrl: createdProject.steamUrl,
          itchioUrl: createdProject.itchioUrl,
          switchUrl: createdProject.switchUrl,
          playstationUrl: createdProject.playstationUrl,
          tags: createdProject.tags || []
        });
        setOpenEdit(true);
      } else {
        // Just pre-fill the edit form
        setEditData(prev => ({
          ...prev,
          ...details
        }));

        if (details.artworkUrl) {
          setArtworkPreview(details.artworkUrl);
        }
      }

      // Clear manual file selection when using IGDB autofill
      setSelectedFile(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error fetching IGDB details');
    } finally {
      setIsSearchingIgdb(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Clear IGDB artwork if a manual file is selected
      setNewProject(prev => ({ ...prev, artworkUrl: undefined } as any));
      setEditData(prev => ({ ...prev, artworkUrl: undefined }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setArtworkPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIgdbSearch = async () => {
    const isNew = openCreate;
    const name = isNew ? newProject.name : editData.name;
    if (!name) return;

    setIsSearchingIgdb(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/games/igdb/search?name=${encodeURIComponent(name)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const results = response.data;
      setIgdbResults(results);

      // Check for exact match (case-insensitive, trim-aware)
      const normalizedSearch = name.trim().toLowerCase();
      const exactMatch = results.find((g: any) => g.name.trim().toLowerCase() === normalizedSearch);

      if (exactMatch) {
        await handleSelectIgdbGame(exactMatch.id, isNew);
      } else {
        if (isNew) {
          setCreateStep('results');
        }
        setShowIgdbResults(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error searching IGDB');
    } finally {
      setIsSearchingIgdb(false);
    }
  };

  const handleAddSocial = async () => {
    if (!selectedProject) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/social-accounts`, {
        ...socialData,
        gameId: selectedProject.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshProjects();
      setOpenSocial(false);
      setSocialData({ platform: 'twitter', name: '', handle: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error adding social account');
    }
  };

  const handleLinkExistingSocial = async (socialAccountId: string) => {
    if (!selectedProject) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/social-accounts/link`, {
        socialAccountId,
        gameId: selectedProject.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshProjects();
      setOpenSocial(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error linking social account');
    }
  };

  return (
    <div className="container mx-auto py-10 px-4 max-w-6xl space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20 shadow-lg shadow-primary/5">
            <Gamepad2 className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-foreground tracking-tight italic uppercase">
              {isGamedev ? 'My Projects' : 'My Project'}
            </h1>
            <p className="text-muted-foreground font-medium">
              {isGamedev ? 'Manage your games and their social connections.' : 'Manage your project and social connections.'}
            </p>
          </div>
        </div>
        {(isGamedev || projects.length === 0) && (
          <Button
            onClick={() => setOpenCreate(true)}
            className="bg-primary hover:bg-primary/90 text-white font-bold h-12 px-6 rounded-xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-all"
          >
            <Plus className="mr-2 h-5 w-5" strokeWidth={3} />
            New Project
          </Button>
        )}
      </div>

      <DowngradeBanner />

      {error && (
        <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projects.map((project) => (
          <Card
            key={project.id}
            className={cn(
              "bg-card border-border transition-all duration-300 hover:shadow-2xl hover:border-border group overflow-hidden relative",
              activeProject?.id === project.id && "ring-2 ring-primary border-transparent shadow-primary/10"
            )}
          >
            {activeProject?.id === project.id && (
              <div className="absolute top-0 right-0 p-3">
                <Badge className="bg-primary text-[#0f172a] font-black uppercase text-[10px] tracking-widest px-2 py-0.5">
                  Active
                </Badge>
              </div>
            )}

            <CardHeader className="pb-4">
              <div className="flex items-start gap-5">
                {/* Larger Cover on the Left */}
                <div className="bg-primary/10 p-0 rounded-2xl border border-primary/20 overflow-hidden h-32 w-24 min-w-[128px] flex items-center justify-center relative shadow-lg group-hover:border-primary/40 transition-colors">
                  {project.hasArtwork ? (
                    <img
                      src={getArtworkUrl(project.id, project.updatedAt, token)}
                      alt={project.name}
                      className="h-full w-full object-cover block"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.opacity = '0';
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector('.fallback-icon')) {
                          const fallback = document.createElement('div');
                          fallback.className = 'fallback-icon absolute inset-0 flex items-center justify-center w-full h-full bg-muted';
                          fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary/40"><path d="M6 12h.01M9 12h.01M12 12h.01M15 12h.01M18 12h.01M4 18h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"/></svg>';
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full bg-muted/50">
                      <Gamepad2 className="h-10 w-10 text-primary/20" />
                    </div>
                  )}
                </div>

                {/* Title and Description on the Right */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-2xl font-black text-foreground leading-tight">
                      {project.name}
                    </CardTitle>
                  </div>

                  <CardDescription className="text-muted-foreground text-sm line-clamp-3 leading-relaxed">
                    {project.description || 'No description provided.'}
                  </CardDescription>

                  {/* Project Level & XP */}
                  {project.levelInfo && (
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-black text-white shadow-sm"
                            style={{ backgroundColor: project.levelInfo.badgeColor }}
                          >
                            {project.levelInfo.level}
                          </div>
                          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Level {project.levelInfo.level}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {project.streakCount > 0 && (
                            <div className="flex items-center gap-1 text-orange-500">
                              <Flame className="h-3.5 w-3.5 fill-current" />
                              <span className="text-[10px] font-black">{project.streakCount}</span>
                            </div>
                          )}
                          <span className="text-[10px] font-bold text-muted-foreground">{project.experience} / {project.levelInfo.nextLevelXp} XP</span>
                        </div>
                      </div>
                      <Progress value={project.levelInfo.progressPercent} className="h-1 bg-card" />
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-1">
                    {project.links && Array.isArray(project.links) && project.links.slice(0, 3).map((link: any, i: number) => (
                      <Badge
                        key={i}
                        variant="outline"
                        className="bg-card/50 border-border text-muted-foreground gap-1.5 py-0.5 cursor-pointer hover:bg-muted transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(link.url, '_blank');
                        }}
                      >
                        {getUrlIcon(link.url, "h-3 w-3")}
                        <span className="max-w-[70px] truncate">{getUrlHostname(link.url)}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[11px] font-black text-muted-foreground uppercase tracking-widest">
                  <Tag className="h-3 w-3" />
                  Project Tags
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.tags && Array.isArray(project.tags) && project.tags.map((tag: string, i: number) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-primary/10 text-primary border-primary/20 font-medium py-1 px-2.5"
                    >
                      {tag}
                    </Badge>
                  ))}
                  {(!project.tags || (project.tags as string[]).length === 0) && (
                    <span className="text-xs text-muted-foreground italic">No tags added yet.</span>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[11px] font-black text-muted-foreground uppercase tracking-widest">
                  <Users className="h-3 w-3" />
                  Collaborators
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.userGames?.map((ug: any) => (
                    <Badge
                      key={ug.user.id}
                      variant="outline"
                      className="bg-background border-border text-foreground font-medium py-1 px-2.5"
                    >
                      {ug.user.name}
                      <span className="ml-1.5 opacity-40 font-bold text-[9px] uppercase tracking-wider">{ug.role}</span>
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[11px] font-black text-muted-foreground uppercase tracking-widest">
                  <Share2 className="h-3 w-3" />
                  Social Accounts
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.socialAccounts?.map((sa: any) => {
                    const color = sa.socialAccount.platform.toLowerCase().includes('twitter') ? 'text-sky-400' :
                      sa.socialAccount.platform.toLowerCase().includes('tiktok') ? 'text-pink-500' :
                        sa.socialAccount.platform.toLowerCase().includes('bluesky') ? 'text-primary' :
                          sa.socialAccount.platform.toLowerCase().includes('youtube') ? 'text-red-500' : 'text-muted-foreground';
                    return (
                      <Badge
                        key={sa.socialAccount.id}
                        variant="secondary"
                        className="bg-background border border-border text-foreground py-1 px-2.5 flex items-center gap-2 group/tag"
                      >
                        <span className={color}>{getPlatformIcon(sa.socialAccount.platform, "h-3.5 w-3.5")}</span>
                        {sa.socialAccount.name}
                      </Badge>
                    );
                  })}
                  {(!project.socialAccounts || project.socialAccounts.length === 0) && (
                    <span className="text-xs text-muted-foreground italic">No accounts linked yet.</span>
                  )}
                </div>
              </div>
            </CardContent>

            <CardFooter className="pt-2 flex items-center justify-between border-t border-border/50 bg-card/20 mt-2">
              <Button
                variant={activeProject?.id === project.id ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setActiveProject(project)}
                disabled={activeProject?.id === project.id}
                className={cn(
                  "font-bold transition-all",
                  activeProject?.id === project.id ? "bg-primary/10 text-primary hover:bg-primary/20" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {activeProject?.id === project.id ? (
                  <><CheckCircle2 className="mr-2 h-4 w-4" /> Current</>
                ) : 'Switch to Project'}
              </Button>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full"
                  onClick={() => {
                    setSelectedProject(project);
                    setOpenSocial(true);
                  }}
                  title="Manage Social Accounts"
                >
                  <Link2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-full"
                  onClick={() => {
                    setSelectedProject(project);
                    setOpenInvite(true);
                  }}
                  title="Invite Collaborators"
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground rounded-full">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-card border-border text-foreground">
                    <DropdownMenuItem
                      className="focus:bg-primary/10 focus:text-primary cursor-pointer"
                      onClick={() => {
                        setSelectedProject(project);
                        setEditData({
                          name: project.name,
                          description: project.description,
                          gameSummary: project.gameSummary,
                          artworkUrl: project.artworkUrl,
                          websiteUrl: project.websiteUrl,
                          steamUrl: project.steamUrl,
                          itchioUrl: project.itchioUrl,
                          switchUrl: project.switchUrl,
                          playstationUrl: project.playstationUrl,
                          tags: project.tags || []
                        });
                        setOpenEdit(true);
                      }}
                    >
                      <Settings className="mr-2 h-4 w-4" /> Edit Details
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                      onClick={() => checkArchiveDependencies(project)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Archive Project
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* Create Project Dialog */}
      <Dialog open={openCreate} onOpenChange={(open) => {
        setOpenCreate(open);
        if (!open) {
          setCreateStep('name');
          setNewProject({ name: '', description: '', links: [], tags: [] });
          setError('');
          setIgdbResults([]);
          setShowIgdbResults(false);
        }
      }}>
        <DialogContent className={cn(
          "bg-card border-border text-foreground transition-all duration-300 overflow-hidden flex flex-col",
          createStep === 'name' ? "max-w-[425px] w-[90vw]" : "max-w-[600px] w-[95vw]"
        )}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <PlusCircle className="h-6 w-6 text-primary" />
              New Project
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {!isGamedev
                ? "Give your project a name to get started."
                : createStep === 'name'
                  ? "Start by entering the name of your game."
                  : `We found ${igdbResults.length} games matching "${newProject.name}"`}
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 text-red-400 py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}

          <div className="py-6 px-2 w-full">
            {/* Simple create for non-gamedev brands */}
            {!isGamedev ? (
              <div className="space-y-6">
                <div className="grid gap-3">
                  <Label htmlFor="create-name" className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Project Name</Label>
                  <div className="relative group">
                    <PlusCircle className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input
                      id="create-name"
                      placeholder="e.g. My Brand"
                      className="bg-background border-border pl-12 h-14 text-lg focus:ring-primary rounded-xl transition-all"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && newProject.name.trim() && handleCreateProject()}
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 rounded-xl transition-all shadow-lg shadow-primary/20 active:scale-[0.98]"
                  onClick={() => handleCreateProject()}
                  disabled={!newProject.name.trim()}
                >
                  <Plus className="mr-2 h-5 w-5" /> Create Project
                </Button>
              </div>
            ) : createStep === 'name' ? (
              <div className="space-y-6">
                <div className="grid gap-3">
                  <Label htmlFor="create-name" className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Game Name</Label>
                  <div className="relative group">
                    <Gamepad2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input
                      id="create-name"
                      placeholder="e.g. Cyberpunk 2077"
                      className="bg-background border-border pl-12 h-14 text-lg focus:ring-primary rounded-xl transition-all"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && handleIgdbSearch()}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 rounded-xl transition-all shadow-lg shadow-primary/20 active:scale-[0.98]"
                    onClick={handleIgdbSearch}
                    disabled={isSearchingIgdb || !newProject.name.trim()}
                  >
                    {isSearchingIgdb ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Searching IGDB...</>
                    ) : (
                      <><Search className="mr-2 h-5 w-5" /> Search Game Details</>
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground hover:text-foreground hover:bg-card/50 h-10 text-xs font-bold rounded-xl"
                    onClick={() => handleCreateProject()}
                    disabled={!newProject.name.trim()}
                  >
                    Skip & Create Manually
                  </Button>
                </div>

                <div className="pt-4 space-y-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <Tag className="h-3 w-3 text-primary" />
                      Game Tags (Optional)
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g. RPG, PvP, Pixel Art"
                      className="bg-background border-border h-10 text-xs"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag(true))}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-10 bg-card hover:bg-muted"
                      onClick={() => handleAddTag(true)}
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 min-h-[40px] p-3 bg-background rounded-xl border border-border shadow-inner">
                    {(newProject.tags || []).map((tag: string) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="bg-primary/10 text-primary border-primary/20 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 cursor-pointer transition-all group"
                        onClick={() => handleRemoveTag(tag, true)}
                      >
                        {tag}
                        <X className="ml-1 h-2 w-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Badge>
                    ))}
                    {(!newProject.tags || newProject.tags.length === 0) && (
                      <span className="text-[10px] text-muted-foreground italic">No tags added yet.</span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full max-w-full flex flex-col">
                <div className="bg-background border border-border rounded-2xl overflow-hidden shadow-2xl w-full relative">
                  <ScrollArea className="h-[350px] w-full">
                    <div className="divide-y divide-border/50 w-full flex flex-col overflow-hidden">
                      {igdbResults.length > 0 ? (
                        igdbResults.map((game) => (
                          <div
                            key={game.id}
                            className="p-4 hover:bg-primary/5 cursor-pointer flex items-center gap-4 transition-all group border-l-4 border-transparent hover:border-primary w-full min-w-0 overflow-hidden"
                            onClick={() => handleSelectIgdbGame(game.id, true)}
                          >
                            <div className="h-16 w-12 min-w-[48px] rounded-lg bg-card overflow-hidden border border-border shadow-sm flex-shrink-0">
                              {game.cover ? (
                                <img
                                  src={`https://images.igdb.com/igdb/image/upload/t_thumb/${game.cover.image_id}.jpg`}
                                  alt=""
                                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                  <Gamepad2 className="h-6 w-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-center space-y-1 overflow-hidden">
                              <div className="text-base font-bold text-foreground truncate group-hover:text-primary transition-colors w-full" title={game.name}>
                                {game.name.length > 45 ? `${game.name.substring(0, 45)}...` : game.name}
                              </div>
                              {game.summary && (
                                <div className="text-xs text-muted-foreground line-clamp-2 leading-relaxed w-full overflow-hidden">
                                  {game.summary}
                                </div>
                              )}
                            </div>
                            <div className="opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0 flex-shrink-0">
                              <div className="bg-primary/10 p-2 rounded-full border border-primary/20">
                                <Wand2 className="h-4 w-4 text-primary" />
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center px-6 space-y-4 w-full">
                          <div className="bg-card/50 p-4 rounded-full">
                            <Search className="h-10 w-10 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-foreground font-bold">No exact matches found</p>
                            <p className="text-xs text-muted-foreground mt-1 max-w-[250px]">
                              We couldn't find any games matching "{newProject.name}" on IGDB.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-border text-muted-foreground hover:bg-card"
                            onClick={() => handleCreateProject()}
                          >
                            Create custom project anyway
                          </Button>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                <div className="flex justify-center pt-2">
                  <Button
                    variant="ghost"
                    className="text-muted-foreground hover:text-foreground hover:bg-transparent h-8 text-xs gap-2 group"
                    onClick={() => setCreateStep('name')}
                  >
                    <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to name search
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Collaborator Dialog */}
      <Dialog open={openInvite} onOpenChange={setOpenInvite}>
        <DialogContent className="bg-card border-border text-foreground sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Invite Collaborator</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Share your project <span className="text-primary font-bold">{selectedProject?.name}</span> with your team.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="grid gap-2">
              <Label className="text-foreground">Email Address</Label>
              <Input
                type="email"
                placeholder="colleague@studio.com"
                className="bg-background border-border"
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-foreground">Access Level</Label>
              <Tabs
                value={inviteData.role}
                onValueChange={(v) => setInviteData({ ...inviteData, role: v })}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-3 bg-background border border-border">
                  <TabsTrigger value="VIEWER" className="data-[state=active]:bg-primary data-[state=active]:text-white">Viewer</TabsTrigger>
                  <TabsTrigger value="EDITOR" className="data-[state=active]:bg-primary data-[state=active]:text-white">Editor</TabsTrigger>
                  <TabsTrigger value="OWNER" className="data-[state=active]:bg-primary data-[state=active]:text-white">Owner</TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="text-[10px] text-muted-foreground mt-1 italic">
                {inviteData.role === 'VIEWER' ? "Can only view analytics and posts." :
                  inviteData.role === 'EDITOR' ? "Can create and schedule posts." :
                    "Full control over project settings and members."}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenInvite(false)} className="text-muted-foreground">Cancel</Button>
            <Button onClick={handleInvite} className="bg-primary hover:bg-primary/90 text-white font-bold">
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social Accounts Management Dialog */}
      <Dialog open={openSocial} onOpenChange={setOpenSocial}>
        <DialogContent className="bg-card border-border text-foreground sm:max-w-[500px] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Link2 className="h-6 w-6 text-primary" />
              Social Connections
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Manage accounts for <span className="text-primary font-bold">{selectedProject?.name}</span>
            </DialogDescription>
          </DialogHeader>

          <Tabs value={socialTab} onValueChange={setSocialTab} className="flex-1 flex flex-col">
            <div className="px-6">
              <TabsList className="grid w-full grid-cols-2 bg-background border border-border h-12 p-1">
                <TabsTrigger value="add-new" className="data-[state=active]:bg-card data-[state=active]:text-primary">Create New</TabsTrigger>
                <TabsTrigger value="link-existing" className="data-[state=active]:bg-card data-[state=active]:text-primary">Link Existing</TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1 px-6 py-6 h-[400px]">
              <TabsContent value="add-new" className="mt-0 space-y-6 animate-in fade-in slide-in-from-right-2 duration-300">
                <div className="space-y-4">
                  <div className="grid gap-2">
                    <Label className="text-muted-foreground text-xs uppercase font-black tracking-widest">Platform</Label>
                    <select
                      className="w-full bg-background border border-border rounded-md h-10 px-3 text-foreground outline-none focus:ring-2 focus:ring-primary transition-all appearance-none cursor-pointer"
                      value={socialData.platform}
                      onChange={(e) => setSocialData({ ...socialData, platform: e.target.value })}
                    >
                      <option value="twitter">X (Twitter)</option>
                      <option value="tiktok">TikTok</option>
                      <option value="instagram">Instagram</option>
                      <option value="youtube">YouTube</option>
                      <option value="facebook">Facebook</option>
                    </select>
                  </div>

                  <div className="grid gap-2">
                    <Label className="text-muted-foreground text-xs uppercase font-black tracking-widest">Internal Label</Label>
                    <Input
                      placeholder="e.g. My Studio Official X"
                      className="bg-background border-border"
                      value={socialData.name}
                      onChange={(e) => setSocialData({ ...socialData, name: e.target.value })}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className="text-muted-foreground text-xs uppercase font-black tracking-widest">Handle / Profile URL</Label>
                    <Input
                      placeholder="@myhandle"
                      className="bg-background border-border"
                      value={socialData.handle}
                      onChange={(e) => setSocialData({ ...socialData, handle: e.target.value })}
                    />
                  </div>

                  <Button
                    className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 rounded-xl mt-4 transition-all active:scale-[0.98]"
                    onClick={handleAddSocial}
                  >
                    Add Connection
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="link-existing" className="mt-0 animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="space-y-3">
                  {allSocialAccounts
                    .filter(account => !selectedProject?.socialAccounts?.some((sa: any) => sa.socialAccount.id === account.id))
                    .map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center justify-between p-4 bg-background rounded-xl border border-border transition-all hover:border-border hover:bg-card/40 group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-muted border border-border flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                            {getPlatformIcon(account.platform, "h-5 w-5")}
                          </div>
                          <div>
                            <div className="text-foreground font-bold">{account.name}</div>
                            <div className="text-muted-foreground text-[10px] uppercase tracking-widest font-black">{account.platform}</div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-primary/30 text-primary hover:bg-primary hover:text-foreground rounded-lg h-8 px-4 font-bold"
                          onClick={() => handleLinkExistingSocial(account.id)}
                        >
                          Link
                        </Button>
                      </div>
                    ))}
                  {allSocialAccounts.filter(account => !selectedProject?.socialAccounts?.some((sa: any) => sa.socialAccount.id === account.id)).length === 0 && (
                    <div className="flex flex-col items-center justify-center py-10 text-center space-y-4">
                      <div className="bg-muted p-4 rounded-full">
                        <Share2 className="h-10 w-10 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground text-sm max-w-[200px]">
                        No more accounts available. Create a new one instead.
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </ScrollArea>

            <div className="p-6 bg-card/30 border-t border-border">
              <Alert className="bg-primary/5 border-primary/20 text-muted-foreground border-none p-0 flex items-start gap-3">
                <Info className="h-4 w-4 text-primary mt-0.5" />
                <AlertDescription className="text-[11px] leading-relaxed">
                  <strong className="text-primary">Important:</strong> Adding an account here only links it to your project. You will need to authenticate each platform later to enable direct posting.
                </AlertDescription>
              </Alert>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={openEdit} onOpenChange={(open) => {
        setOpenEdit(open);
        if (!open) {
          setSelectedFile(null);
          setArtworkPreview(null);
        }
      }}>
        <DialogContent className="bg-card border-border text-foreground sm:max-w-[850px] max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="text-2xl font-bold">Edit Project Details</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Update your project information and store links.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-4">
              {/* Left Column: Core Info & Artwork */}
              <div className="space-y-6">
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-name" className="text-foreground">Project Name</Label>
                    {isGamedev && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px] font-black uppercase tracking-widest bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
                        onClick={handleIgdbSearch}
                        disabled={isSearchingIgdb}
                      >
                        {isSearchingIgdb ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Wand2 className="h-3 w-3 mr-1.5" />}
                        Autofill with IGDB
                      </Button>
                    )}
                  </div>
                  <Input
                    id="edit-name"
                    placeholder="Cyberpunk 2077 Content"
                    className="bg-background border-border focus:ring-primary"
                    value={editData.name || ''}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    onKeyDown={(e) => isGamedev && e.key === 'Enter' && (e.preventDefault(), handleIgdbSearch())}
                  />
                </div>

                {isGamedev && showIgdbResults && igdbResults.length > 0 && (
                  <div className="bg-background border border-border rounded-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="p-3 bg-muted/50 border-b border-border flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Select your game</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full" onClick={() => setShowIgdbResults(false)}>
                        <Plus className="h-3 w-3 rotate-45" />
                      </Button>
                    </div>
                    <div className="divide-y divide-border">
                      {igdbResults.map((game) => (
                        <div
                          key={game.id}
                          className="p-3 hover:bg-card/50 cursor-pointer flex items-center gap-3 transition-colors"
                          onClick={() => handleSelectIgdbGame(game.id, false)}
                        >
                          {game.cover ? (
                            <img
                              src={`https://images.igdb.com/igdb/image/upload/t_thumb/${game.cover.image_id}.jpg`}
                              alt=""
                              className="h-10 w-8 rounded object-cover border border-border"
                            />
                          ) : (
                            <div className="h-10 w-8 rounded bg-card border border-border flex items-center justify-center">
                              <Gamepad2 className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-bold text-foreground">{game.name}</div>
                            {game.summary && <div className="text-[10px] text-muted-foreground line-clamp-1">{game.summary}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="edit-description" className="text-foreground">Description</Label>
                  <Textarea
                    id="edit-description"
                    className="bg-background border-border min-h-[120px]"
                    value={editData.description || ''}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  />
                </div>

                {isGamedev && <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit-game-summary" className="text-foreground">AI Game Summary</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-primary hover:text-primary/80"
                      disabled={isRegeneratingSummary}
                      onClick={async () => {
                        if (!selectedProject) return;
                        setIsRegeneratingSummary(true);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`${API_BASE_URL}/games/${selectedProject.id}/regenerate-summary`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setEditData({ ...editData, gameSummary: res.data.gameSummary });
                          toast.success('Game summary regenerated');
                        } catch (err) {
                          toast.error('Failed to regenerate summary');
                        } finally {
                          setIsRegeneratingSummary(false);
                        }
                      }}
                    >
                      {isRegeneratingSummary ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                      Regenerate
                    </Button>
                  </div>
                  <Textarea
                    id="edit-game-summary"
                    className="bg-background border-border min-h-[100px] text-xs"
                    placeholder="AI-generated summary used as context for caption generation..."
                    value={editData.gameSummary || ''}
                    onChange={(e) => setEditData({ ...editData, gameSummary: e.target.value })}
                  />
                  <p className="text-[9px] text-muted-foreground leading-tight">
                    This summary is auto-generated from IGDB data and used as context for AI caption generation.
                  </p>
                </div>}

                {isGamedev && <div className="grid gap-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground font-bold flex items-center gap-2">
                      <Tag className="h-3 w-3 text-primary" />
                      Game Tags & Categories
                    </Label>
                    <span className="text-[10px] text-muted-foreground uppercase font-black">Used for Steam matching</span>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      placeholder="Add tag (e.g. RPG, PvP, Pixel Art)"
                      className="bg-background border-border h-9 text-xs"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag(false))}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-9 bg-card hover:bg-muted"
                      onClick={() => handleAddTag(false)}
                    >
                      Add
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-1.5 min-h-[40px] p-3 bg-background rounded-xl border border-border shadow-inner">
                    {(editData.tags || []).map((tag: string) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="bg-primary/10 text-primary border-primary/20 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 cursor-pointer transition-all group"
                        onClick={() => handleRemoveTag(tag, false)}
                      >
                        {tag}
                        <X className="ml-1 h-2 w-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Badge>
                    ))}
                    {(!editData.tags || editData.tags.length === 0) && (
                      <span className="text-[10px] text-muted-foreground italic">No tags added yet.</span>
                    )}
                  </div>

                  <p className="text-[9px] text-muted-foreground leading-tight">
                    Tags help us match your game with upcoming Steam events and festivals.
                  </p>
                </div>}

                <div className="grid gap-2">
                  <Label className="text-foreground font-bold">{isGamedev ? 'Game Cover (Avatar)' : 'Project Cover'}</Label>
                  <div className="flex items-center gap-4">
                    <div className="h-24 w-24 min-w-[96px] rounded-xl bg-background border border-border overflow-hidden flex items-center justify-center shadow-inner relative bg-black/40">
                      {artworkPreview ? (
                        <img
                          src={artworkPreview}
                          alt="Game Cover Preview"
                          className="h-full w-full object-cover block"
                          onError={(e) => {
                            console.error("Image failed to load:", artworkPreview);
                            const target = e.target as HTMLImageElement;
                            target.style.opacity = '0.3';
                          }}
                        />
                      ) : (selectedProject && selectedProject.hasArtwork && !selectedFile) ? (
                        <img
                          src={getArtworkUrl(selectedProject.id, selectedProject.updatedAt, token)}
                          alt="Current Project Cover"
                          className="h-full w-full object-cover block"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.opacity = '0';
                            const parent = target.parentElement;
                            if (parent && !parent.querySelector('.fallback-icon')) {
                              const fallback = document.createElement('div');
                              fallback.className = 'fallback-icon absolute inset-0 flex items-center justify-center w-full h-full bg-muted';
                              fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary/40"><path d="M6 12h.01M9 12h.01M12 12h.01M15 12h.01M18 12h.01M4 18h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"/></svg>';
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-1">
                          <Gamepad2 className="h-8 w-8 text-muted-foreground" />
                          <span className="text-[8px] text-muted-foreground font-bold uppercase">No Cover</span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 space-y-2">
                      {artworkPreview && artworkPreview.startsWith('http') && !selectedFile ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg p-2.5">
                            <div className="bg-primary/20 p-1.5 rounded-md">
                              <Wand2 className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1">
                              <p className="text-[11px] text-primary font-black uppercase tracking-tight">IGDB Image Selected</p>
                              <p className="text-[9px] text-muted-foreground leading-tight">This high-quality cover will be saved to your project.</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-[10px] border-border hover:bg-card text-muted-foreground"
                              onClick={() => {
                                setArtworkPreview(null);
                                setNewProject(prev => ({ ...prev, artworkUrl: undefined } as any));
                                setEditData(prev => ({ ...prev, artworkUrl: undefined }));
                              }}
                            >
                              Change
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Input
                            type="file"
                            accept="image/*"
                            className="bg-background border-border text-xs file:bg-card file:text-foreground file:border-none file:mr-4 file:px-4 file:py-1 file:rounded-md hover:file:bg-muted cursor-pointer"
                            onChange={handleFileChange}
                          />
                          <p className="text-[10px] text-muted-foreground">Square JPG/PNG recommended. Or use <b>Autofill</b> above.</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Links */}
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2">
                    <Link2 className="h-3 w-3" />
                    Store & Website Links
                  </div>

                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Add a new URL (Steam, Twitter, etc.)"
                          className="bg-background border-border pl-9"
                          value={newLinkUrl}
                          onChange={(e) => setNewLinkUrl(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLink(false))}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        className="bg-card hover:bg-muted text-foreground"
                        onClick={() => handleAddLink(false)}
                      >
                        <PlusCircle className="h-4 w-4 mr-2" /> Add
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 gap-2">
                      {(editData.links || []).map((link: any, index: number) => (
                        <div
                          key={index}
                          className="flex flex-col p-3 bg-background rounded-lg border border-border group hover:border-border transition-all relative"
                        >
                          <div className="flex items-center gap-3 mb-1">
                            <div className="h-7 w-7 rounded bg-muted border border-border flex items-center justify-center text-primary flex-shrink-0">
                              {getUrlIcon(link.url)}
                            </div>
                            <div className="text-xs font-bold text-foreground truncate">{getUrlHostname(link.url)}</div>
                            <div className="flex-1" />
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {isGamedev && link.url.includes('steampowered.com') && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-primary hover:text-primary/80 hover:bg-primary/10"
                                  onClick={() => handleScrapeSteamTags(link.url, false)}
                                  disabled={isScrapingTags}
                                  title="Import tags from Steam"
                                >
                                  {isScrapingTags ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-primary"
                                onClick={() => window.open(link.url, '_blank')}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-red-400"
                                onClick={() => handleRemoveLink(index, false)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate pl-10">{link.url}</div>
                        </div>
                      ))}
                      {(!editData.links || editData.links.length === 0) && (
                        <div className="text-center py-6 border-2 border-dashed border-border rounded-xl">
                          <p className="text-xs text-muted-foreground italic">No links added yet. Use Autofill or add manually.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="mt-4 border-t border-border pt-4 p-6 bg-card/20">
            <Button variant="ghost" onClick={() => setOpenEdit(false)} className="text-muted-foreground">Cancel</Button>
            <Button onClick={handleUpdateProject} className="bg-primary hover:bg-primary/90 text-white font-bold px-8">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Archive Confirmation Dialog */}
      <Dialog open={!!projectToArchive} onOpenChange={(open) => !open && !isArchiving && setProjectToArchive(null)}>
        <DialogContent className="max-w-md bg-card border-border text-foreground rounded-3xl shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-foreground flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Archive Project
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to archive <strong>{projectToArchive?.name}</strong>? It will be removed from your active list.
            </DialogDescription>
          </DialogHeader>

          {checkingDependencies ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">Checking for scheduled posts...</p>
            </div>
          ) : archiveDependencies?.pendingSchedules?.length > 0 ? (
            <div className="space-y-4 py-2">
              <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400 rounded-2xl border-2">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle className="font-black uppercase tracking-widest text-[10px]">Warning: Scheduled Posts Detected</AlertTitle>
                <AlertDescription className="text-xs font-medium">
                  This project has pending scheduled posts. If you archive it, all of them will be **permanently cancelled**:
                </AlertDescription>
              </Alert>

              <ScrollArea className="h-32 border border-border rounded-xl bg-muted/50 shadow-inner">
                <div className="p-3 space-y-2">
                  {archiveDependencies.pendingSchedules.map((s: any) => (
                    <div key={s.id} className="flex items-center justify-between text-[10px] bg-background/50 p-2.5 rounded-lg border border-border/50 group/item">
                      <div className="flex flex-col min-w-0">
                        <span className="text-foreground font-bold truncate">{s.socialAccount.name}</span>
                        <span className="text-muted-foreground font-mono">{moment(s.scheduledAt).format('MMM DD, HH:mm')}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[9px] text-primary hover:text-primary/80 hover:bg-primary/10 gap-1.5 px-3 bg-primary/5 border border-primary/10 rounded-md transition-all"
                        onClick={() => {
                          setProjectToArchive(null);
                          openPostById(s.postId);
                        }}
                      >
                        View in Agenda <ChevronRight className="h-3 w-3 group-hover/item:translate-x-0.5 transition-transform" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="py-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                You can restore this project later from the archived projects section.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button
              variant="outline"
              onClick={() => setProjectToArchive(null)}
              disabled={isArchiving}
              className="border-border text-foreground hover:bg-card rounded-xl px-6"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => projectToArchive && handleArchiveProject(projectToArchive.id)}
              disabled={isArchiving || checkingDependencies}
              className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl gap-2 px-6 shadow-lg shadow-red-500/20"
            >
              {isArchiving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Archive & Cancel Posts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
